import { jsPDF } from "jspdf";

const PAGE_WIDTH_MM = 50;
const PAGE_HEIGHT_MM = 50;
const QR_SIZE_MM = 24;
const CONTENT_WIDTH_MM = 44;
const TEXT_CENTER_X_MM = PAGE_WIDTH_MM / 2;
const QR_TO_NAME_GAP_MM = 1.5;
const NAME_TO_COMPANY_GAP_MM = 1;
const NAME_FONT_SIZE_PT = 9;
const NAME_LINE_HEIGHT_FACTOR = 1.15;
const COMPANY_FONT_SIZE_PT = 9.5;
const COMPANY_MIN_FONT_SIZE_PT = 7;
const COMPANY_LINE_HEIGHT_FACTOR = 1.1;
const MAX_COMPANY_LINES = 2;
const POINTS_TO_MM = 25.4 / 72;

const readBlobAsDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Unable to read QR image"));
    reader.readAsDataURL(blob);
  });

const loadQrImage = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Unable to load QR image (${response.status})`);
  }

  const blob = await response.blob();
  const dataUrl = await readBlobAsDataUrl(blob);
  const format = blob.type.includes("jpeg") || blob.type.includes("jpg") ? "JPEG" : "PNG";

  return { dataUrl, format };
};

const fitCompanyText = (pdf, text, maxWidth, initialSize, minimumSize) => {
  let fontSize = initialSize;
  let lines = [];

  while (fontSize >= minimumSize) {
    pdf.setFontSize(fontSize);
    lines = pdf.splitTextToSize(text, maxWidth);
    if (lines.length <= MAX_COMPANY_LINES) break;
    fontSize -= 0.5;
  }

  pdf.setFontSize(Math.max(fontSize, minimumSize));
  return {
    fontSize: Math.max(fontSize, minimumSize),
    lines: lines.slice(0, MAX_COMPANY_LINES),
  };
};

const addLabelPage = (pdf, label, qrImage, pageIndex) => {
  if (pageIndex > 0) {
    pdf.addPage([PAGE_WIDTH_MM, PAGE_HEIGHT_MM], "portrait");
  }

  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, PAGE_WIDTH_MM, PAGE_HEIGHT_MM, "F");

  const name = String(label.name || "-").trim() || "-";
  const company = String(label.company || "-").trim() || "-";

  pdf.setTextColor(15, 23, 42);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(NAME_FONT_SIZE_PT);

  const nameLines = pdf.splitTextToSize(name, CONTENT_WIDTH_MM).slice(0, 2);
  const nameLineHeightMm =
    NAME_FONT_SIZE_PT * NAME_LINE_HEIGHT_FACTOR * POINTS_TO_MM;

  pdf.setFont("helvetica", "bold");
  const fittedCompany = fitCompanyText(
    pdf,
    company,
    CONTENT_WIDTH_MM,
    COMPANY_FONT_SIZE_PT,
    COMPANY_MIN_FONT_SIZE_PT
  );
  const companyLineHeightMm =
    fittedCompany.fontSize * COMPANY_LINE_HEIGHT_FACTOR * POINTS_TO_MM;
  const contentHeight =
    QR_SIZE_MM +
    QR_TO_NAME_GAP_MM +
    Math.max(nameLines.length, 1) * nameLineHeightMm +
    NAME_TO_COMPANY_GAP_MM +
    Math.max(fittedCompany.lines.length, 1) * companyLineHeightMm;
  const qrTop = (PAGE_HEIGHT_MM - contentHeight) / 2;

  pdf.addImage(
    qrImage.dataUrl,
    qrImage.format,
    (PAGE_WIDTH_MM - QR_SIZE_MM) / 2,
    qrTop,
    QR_SIZE_MM,
    QR_SIZE_MM
  );

  const nameFontHeightMm = NAME_FONT_SIZE_PT * POINTS_TO_MM;
  const nameStartY =
    qrTop + QR_SIZE_MM + QR_TO_NAME_GAP_MM + nameFontHeightMm;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(NAME_FONT_SIZE_PT);
  pdf.text(nameLines, TEXT_CENTER_X_MM, nameStartY, {
    align: "center",
    lineHeightFactor: NAME_LINE_HEIGHT_FACTOR,
  });

  const companyY =
    qrTop +
    QR_SIZE_MM +
    QR_TO_NAME_GAP_MM +
    Math.max(nameLines.length, 1) * nameLineHeightMm +
    NAME_TO_COMPANY_GAP_MM +
    fittedCompany.fontSize * POINTS_TO_MM;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(fittedCompany.fontSize);
  pdf.text(fittedCompany.lines, TEXT_CENTER_X_MM, companyY, {
    align: "center",
    lineHeightFactor: COMPANY_LINE_HEIGHT_FACTOR,
  });
};

export const printQrLabelsAsPdf = async (
  labels,
  existingWindow = null,
  options = {}
) => {
  const printableLabels = labels.filter((label) => label?.qrUrl);
  if (printableLabels.length === 0) {
    throw new Error("No QR labels are available to print");
  }

  const printWindow = existingWindow || window.open("", "_blank");
  if (!printWindow) {
    const error = new Error("Popup blocked. Please allow popups to print QR labels.");
    error.code = "POPUP_BLOCKED";
    throw error;
  }

  printWindow.document.write(
    "<!doctype html><title></title><body style=\"margin:0;display:grid;place-items:center;height:100vh;font:16px Arial;color:#334155\">Preparing QR label…</body>"
  );
  printWindow.document.close();

  try {
    const qrImageResults = await Promise.allSettled(
      printableLabels.map((label) => loadQrImage(label.qrUrl))
    );
    const loadedLabels = [];
    const qrImages = [];
    const skippedLabels = [];

    qrImageResults.forEach((result, index) => {
      if (result.status === "fulfilled") {
        loadedLabels.push(printableLabels[index]);
        qrImages.push(result.value);
      } else {
        skippedLabels.push({
          label: printableLabels[index],
          error: result.reason,
        });
      }
    });

    if (loadedLabels.length === 0) {
      throw (
        skippedLabels[0]?.error ||
        new Error("Unable to load any QR images for printing")
      );
    }

    if (
      skippedLabels.length > 0 &&
      typeof options.onSkipped === "function"
    ) {
      options.onSkipped(skippedLabels);
    }

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [PAGE_WIDTH_MM, PAGE_HEIGHT_MM],
      compress: true,
    });
    pdf.viewerPreferences({
      PrintScaling: "None",
      PickTrayByPDFSize: true,
    });

    loadedLabels.forEach((label, index) => {
      addLabelPage(pdf, label, qrImages[index], index);
    });

    pdf.autoPrint();
    const pdfUrl = URL.createObjectURL(pdf.output("blob"));
    printWindow.location.replace(pdfUrl);
    window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 120000);

    return printWindow;
  } catch (error) {
    printWindow.close();
    throw error;
  }
};
